import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { format, addDays, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { 
  Calendar as CalendarIcon, 
  Users, 
  Dog,
  ChevronRight,
  Loader2,
  Check,
  Clock,
  AlertCircle
} from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError } from '../lib/api';
import { formatCurrency } from '../lib/utils';

const VILLA_IMAGE = "https://images.unsplash.com/photo-1774280960001-ce8169cfc38f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB2aWxsYSUyMHBvb2wlMjBuYXR1cmV8ZW58MHx8fHwxNzc1MDc5MTgxfDA&ixlib=rb-4.1.0&q=85";

const BOOKING_DRAFT_KEY = 'finca.booking.draft';

function readDraft() {
  try {
    const raw = localStorage.getItem(BOOKING_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [blockedDates, setBlockedDates] = useState([]);
  const [priceData, setPriceData] = useState(null);

  // Hydrate initial state: URL params > localStorage draft > defaults
  const draft = readDraft();

  const initialType = searchParams.get('type') || draft?.reservationType || 'hospedaje';
  const initialCheckIn = searchParams.get('checkIn')
    ? new Date(searchParams.get('checkIn'))
    : (draft?.checkIn ? new Date(draft.checkIn) : null);
  const initialCheckOut = searchParams.get('checkOut')
    ? new Date(searchParams.get('checkOut'))
    : (draft?.checkOut ? new Date(draft.checkOut) : null);
  const initialGuests = searchParams.get('guests') || draft?.guests || '2';
  const initialPets = draft?.pets || '0';
  const initialSpecialRequests = draft?.specialRequests || '';

  // Form state
  const [reservationType, setReservationType] = useState(initialType);
  const [checkIn, setCheckIn] = useState(initialCheckIn);
  const [checkOut, setCheckOut] = useState(initialCheckOut);
  const [guests, setGuests] = useState(initialGuests);
  const [pets, setPets] = useState(initialPets);
  const [specialRequests, setSpecialRequests] = useState(initialSpecialRequests);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);

  // If user just logged in and had a draft, jump to step 2 automatically
  useEffect(() => {
    if (isAuthenticated && draft && (draft.checkIn || draft.checkOut)) {
      if (draft.reservationType === 'dia' ? draft.checkIn : (draft.checkIn && draft.checkOut)) {
        setStep(2);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    fetchBlockedDates();
  }, []);

  useEffect(() => {
    if (checkIn && (reservationType === 'dia' || checkOut)) {
      calculatePrice();
    }
  }, [checkIn, checkOut, guests, pets, reservationType]);

  const fetchBlockedDates = async () => {
    try {
      const response = await api.get('/blocked-dates');
      const dates = [];
      
      response.data.blocked?.forEach(b => {
        dates.push(new Date(b.date));
      });
      
      response.data.reservations?.forEach(r => {
        const start = new Date(r.check_in);
        const end = r.check_out ? new Date(r.check_out) : start;
        let current = start;
        while (current < end) {
          dates.push(new Date(current));
          current = addDays(current, 1);
        }
      });
      
      setBlockedDates(dates);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  };

  const calculatePrice = async () => {
    try {
      const params = new URLSearchParams({
        check_in: format(checkIn, 'yyyy-MM-dd'),
        guests: guests,
        pets: pets,
        reservation_type: reservationType
      });
      
      if (reservationType === 'hospedaje' && checkOut) {
        params.set('check_out', format(checkOut, 'yyyy-MM-dd'));
      }

      const response = await api.post(`/calculate-price?${params.toString()}`);
      setPriceData(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;
    
    return blockedDates.some(
      blocked => blocked.toDateString() === date.toDateString()
    );
  };

  const nights = checkIn && checkOut ? differenceInDays(checkOut, checkIn) : 0;

  const handleContinue = () => {
    if (step === 1) {
      if (!checkIn) {
        setError('Selecciona una fecha de llegada');
        return;
      }
      if (reservationType === 'hospedaje' && !checkOut) {
        setError('Selecciona una fecha de salida');
        return;
      }
      setError('');
      if (!isAuthenticated) {
        try {
          localStorage.setItem(BOOKING_DRAFT_KEY, JSON.stringify({
            reservationType,
            checkIn: checkIn ? format(checkIn, 'yyyy-MM-dd') : null,
            checkOut: checkOut ? format(checkOut, 'yyyy-MM-dd') : null,
            guests,
            pets,
            specialRequests,
          }));
        } catch {}
        navigate('/login?redirect=/reservar');
        return;
      }
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/reservations', {
        reservation_type: reservationType,
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: reservationType === 'hospedaje' && checkOut ? format(checkOut, 'yyyy-MM-dd') : null,
        guests: parseInt(guests),
        pets: parseInt(pets),
        special_requests: specialRequests || null
      });

      try { localStorage.removeItem(BOOKING_DRAFT_KEY); } catch {}
      navigate(`/reservacion-enviada?id=${response.data.reservation_id}`);
    } catch (error) {
      setError(formatApiError(error));
      setLoading(false);
    }
  };

  return (
    <div data-testid="booking-page" className="pt-20 min-h-screen bg-[#FAF7F2]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-2">
            Solicita Tu Reservación
          </h1>
          <p className="text-[#1A1A1A]/60">
            Completa los datos y te confirmaremos la disponibilidad
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12">
          {[
            { num: 1, label: 'Fechas' },
            { num: 2, label: 'Confirmar' }
          ].map((s, i) => (
            <React.Fragment key={s.num}>
              <div className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                    step >= s.num
                      ? 'bg-[#5C4033] text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step > s.num ? <Check className="h-5 w-5" /> : s.num}
                </div>
                <span className={`ml-2 hidden sm:block ${step >= s.num ? 'text-[#1A1A1A]' : 'text-gray-400'}`}>
                  {s.label}
                </span>
              </div>
              {i < 1 && (
                <div className={`w-12 sm:w-24 h-1 mx-2 ${step > s.num ? 'bg-[#5C4033]' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl p-6 md:p-8 shadow-sm"
            >
              {/* Step 1: Dates */}
              {step === 1 && (
                <div className="space-y-6">
                  <h2 className="font-serif text-2xl text-[#1A1A1A]">
                    Selecciona tus fechas
                  </h2>

                  {/* Reservation Type */}
                  <div>
                    <Label className="text-[#5C4033]">Tipo de Reservación</Label>
                    <RadioGroup
                      value={reservationType}
                      onValueChange={setReservationType}
                      className="grid grid-cols-2 gap-4 mt-2"
                    >
                      <Label
                        htmlFor="hospedaje"
                        className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                          reservationType === 'hospedaje'
                            ? 'border-[#5C4033] bg-[#5C4033]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <RadioGroupItem value="hospedaje" id="hospedaje" />
                        <div>
                          <p className="font-medium">Hospedaje</p>
                          <p className="text-sm text-gray-500">Por noche(s)</p>
                        </div>
                      </Label>
                      <Label
                        htmlFor="dia"
                        className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                          reservationType === 'dia'
                            ? 'border-[#5C4033] bg-[#5C4033]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <RadioGroupItem value="dia" id="dia" />
                        <div>
                          <p className="font-medium">Renta de Día</p>
                          <p className="text-sm text-gray-500">9:00 AM - 6:00 PM</p>
                        </div>
                      </Label>
                    </RadioGroup>
                  </div>

                  {/* Dates */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#5C4033]">
                        {reservationType === 'dia' ? 'Fecha' : 'Llegada'}
                      </Label>
                      <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            data-testid="booking-checkin-btn"
                            className="w-full justify-start text-left mt-2"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 text-[#C8962D]" />
                            {checkIn ? format(checkIn, 'dd MMM yyyy', { locale: es }) : 'Seleccionar'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={checkIn}
                            onSelect={(date) => {
                              setCheckIn(date);
                              setCheckInOpen(false);
                              if (reservationType === 'dia') {
                                setCheckOut(null);
                              } else if (date) {
                                setCheckOut(null);
                                setTimeout(() => setCheckOutOpen(true), 150);
                              }
                            }}
                            disabled={isDateDisabled}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {reservationType === 'hospedaje' && (
                      <div>
                        <Label className="text-[#5C4033]">Salida</Label>
                        <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              data-testid="booking-checkout-btn"
                              className="w-full justify-start text-left mt-2"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 text-[#C8962D]" />
                              {checkOut ? format(checkOut, 'dd MMM yyyy', { locale: es }) : 'Seleccionar'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={checkOut}
                              onSelect={(date) => {
                                setCheckOut(date);
                                setCheckOutOpen(false);
                              }}
                              disabled={(date) => {
                                if (isDateDisabled(date)) return true;
                                if (checkIn && date <= checkIn) return true;
                                return false;
                              }}
                              defaultMonth={checkIn || undefined}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    )}
                  </div>

                  {/* Guests & Pets */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#5C4033]">Huéspedes</Label>
                      <Select value={guests} onValueChange={setGuests}>
                        <SelectTrigger className="mt-2" data-testid="booking-guests-select">
                          <Users className="mr-2 h-4 w-4 text-[#C8962D]" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[...Array(10)].map((_, i) => (
                            <SelectItem key={i + 1} value={String(i + 1)}>
                              {i + 1} {i === 0 ? 'huésped' : 'huéspedes'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#5C4033]">Mascotas</Label>
                      <Select value={pets} onValueChange={setPets}>
                        <SelectTrigger className="mt-2" data-testid="booking-pets-select">
                          <Dog className="mr-2 h-4 w-4 text-[#C8962D]" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[0, 1, 2, 3].map((num) => (
                            <SelectItem key={num} value={String(num)}>
                              {num === 0 ? 'Sin mascotas' : `${num} mascota${num > 1 ? 's' : ''}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Confirm */}
              {step === 2 && (
                <div className="space-y-6">
                  <h2 className="font-serif text-2xl text-[#1A1A1A]">
                    Confirma tu solicitud
                  </h2>

                  <div className="bg-[#FAF7F2] rounded-xl p-4">
                    <p className="text-sm text-[#5C4033] mb-2">Solicitando como:</p>
                    <p className="font-medium text-[#1A1A1A]">{user?.name}</p>
                    <p className="text-[#1A1A1A]/60">{user?.email}</p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">¿Cómo funciona?</p>
                        <ol className="text-sm text-amber-700 mt-2 space-y-1 list-decimal list-inside">
                          <li>Envías tu solicitud de reservación</li>
                          <li>Verificamos disponibilidad y te confirmamos</li>
                          <li>Tienes 4 horas para realizar tu apartado</li>
                          <li>Una vez confirmado el pago, tu fecha queda reservada</li>
                        </ol>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-[#5C4033]">Peticiones especiales (opcional)</Label>
                    <Textarea
                      value={specialRequests}
                      onChange={(e) => setSpecialRequests(e.target.value)}
                      placeholder="¿Alguna solicitud especial para tu estancia?"
                      className="mt-2"
                      data-testid="special-requests-input"
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                {step > 1 && (
                  <Button
                    variant="outline"
                    onClick={() => setStep(step - 1)}
                    disabled={loading}
                  >
                    Atrás
                  </Button>
                )}
                <div className="ml-auto">
                  {step < 2 ? (
                    <Button
                      onClick={handleContinue}
                      data-testid="booking-continue-btn"
                      className="bg-[#5C4033] hover:bg-[#3D5A3E] text-white"
                    >
                      Continuar
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={loading}
                      data-testid="booking-submit-btn"
                      className="bg-[#5C4033] hover:bg-[#3D5A3E] text-white"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          Enviar Solicitud
                          <ChevronRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">
              <div className="aspect-video rounded-xl overflow-hidden mb-4">
                <img src={VILLA_IMAGE} alt="Villa" className="w-full h-full object-cover" />
              </div>

              <h3 className="font-serif text-xl text-[#1A1A1A] mb-4">
                Resumen de Solicitud
              </h3>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#1A1A1A]/60">Tipo</span>
                  <span className="font-medium">{reservationType === 'hospedaje' ? 'Hospedaje' : 'Renta de Día'}</span>
                </div>
                {checkIn && (
                  <div className="flex justify-between">
                    <span className="text-[#1A1A1A]/60">{reservationType === 'dia' ? 'Fecha' : 'Llegada'}</span>
                    <span className="font-medium">{format(checkIn, 'dd MMM yyyy', { locale: es })}</span>
                  </div>
                )}
                {reservationType === 'hospedaje' && checkOut && (
                  <div className="flex justify-between">
                    <span className="text-[#1A1A1A]/60">Salida</span>
                    <span className="font-medium">{format(checkOut, 'dd MMM yyyy', { locale: es })}</span>
                  </div>
                )}
                {nights > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#1A1A1A]/60">Noches</span>
                    <span className="font-medium">{nights}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[#1A1A1A]/60">Huéspedes</span>
                  <span className="font-medium">{guests}</span>
                </div>
                {parseInt(pets) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#1A1A1A]/60">Mascotas</span>
                    <span className="font-medium">{pets}</span>
                  </div>
                )}
              </div>

              {priceData && (
                <>
                  <div className="border-t mt-4 pt-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#1A1A1A]/60">
                        {reservationType === 'hospedaje' ? `${nights} noche(s)` : 'Renta de día'}
                      </span>
                      <span>{formatCurrency(priceData.base_price)}</span>
                    </div>
                    {priceData.extra_guest_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-[#1A1A1A]/60">Huéspedes extra</span>
                        <span>{formatCurrency(priceData.extra_guest_fee)}</span>
                      </div>
                    )}
                    {priceData.pet_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-[#1A1A1A]/60">Mascotas</span>
                        <span>{formatCurrency(priceData.pet_fee)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-[#1A1A1A]/60">Limpieza</span>
                      <span>{formatCurrency(priceData.cleaning_fee)}</span>
                    </div>
                    {priceData.discount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Descuento</span>
                        <span>-{formatCurrency(priceData.discount)}</span>
                      </div>
                    )}
                  </div>

                  <div className="border-t mt-4 pt-4 space-y-2">
                    <div className="flex justify-between text-lg font-serif">
                      <span className="text-[#1A1A1A]">Total</span>
                      <span className="text-[#5C4033]">{formatCurrency(priceData.total)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#1A1A1A]/60">Apartado ({priceData.deposit_percentage}%)</span>
                      <span className="font-medium text-[#C8962D]">{formatCurrency(priceData.deposit_amount)}</span>
                    </div>
                  </div>
                </>
              )}

              <p className="text-xs text-[#1A1A1A]/50 mt-4">
                * Los precios son estimados y serán confirmados por el administrador.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
